import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  Category,
  Tooltip,
  Inject,
  Legend,
} from "@syncfusion/ej2-react-charts";
import dayjs from "dayjs";
import { formatNumber } from "../../utils/formatNumber";


import { ChartControls } from "../../components/Channel";
import { authFetch } from "../../utils/authFetch";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
import "./ChannelChannelGridComparison.css";

import "../../components/charts3/Chart.css";


const SERIES_CONFIG = [
  { key: "current", name: "Current", color: "#DC143C", type: "internal" },
  { key: "lastWeek", name: "Last Week", color: "#4169E1", type: "internal" },
  { key: "yesterday", name: "Yesterday", color: "#228B22", type: "internal" },
  { key: "externalCurrent", name: "External Current", color: "#800080", type: "external" },
  { key: "externalLastWeek", name: "External Last Week", color: "#FF8C00", type: "external" },
  { key: "externalYesterday", name: "External Yesterday", color: "#000000", type: "external" },
];


function minuteToLabel(i) {
  const h = String(Math.floor(i / 60)).padStart(2, "0");
  const m = String(i % 60).padStart(2, "0");
  return `${h}:${m}`;
}

export default function ChannelChannelGridComparison({ channelId, refreshKey, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh, rawVideo }) {
  const [startTime, setStartTime] = useState("00:00");
  
  const [endTime, setEndTime] = useState("23:59");
  const [visibleSeries, setVisibleSeries] = useState(
    SERIES_CONFIG.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );
  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  const isDark = !!storedUser.darkmode;
  const [externalData, setExternalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    date: dayjs().format("YYYY-MM-DD"),
    alias: "",
    file: null,
  });

  // Independent date state for this chart
  const [endDateInput, setEndDateInput] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });

  // Calculate start date based on mode (minute-only mode)
  const startDateInput = useMemo(() => {
    if (!endDateInput) return endDateInput;
    return endDateInput; // minute mode: start = end
  }, [endDateInput]);

  // Chart ref for refresh functionality
  const chartRef = useRef(null);

  // Refresh chart when refreshKey changes
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.refresh();
  }, [refreshKey]);

  // Fetch files list
  const fetchFiles = useCallback(async () => {
    try {
      const response = await authFetch(`/api/external-data/files?channel_id=${channelId}`);
      const result = await response.json();
      if (result.success && result.data) {
        setFiles(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch files:", error);
    }
  }, [channelId]);

  // Fetch external data when date changes
  useEffect(() => {
    if (!startDateInput || !channelId) return;

    const fetchExternalData = async () => {
      setLoading(true);
      try {
        const response = await authFetch(
          `/api/external-data?channel_id=${channelId}&date=${startDateInput}`
        );
        
        // Check if response is ok
        if (!response.ok) {
          console.warn(`External data API returned ${response.status}`);
          setExternalData(null);
          return;
        }

        // Check content type
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.warn("External data API did not return JSON");
          setExternalData(null);
          return;
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          setExternalData(result.data);
        } else {
          setExternalData(null);
        }
      } catch (error) {
        console.error("Failed to fetch external data:", error);
        setExternalData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchExternalData();
 }, [startDateInput, channelId, refreshKey]);


  // Fetch files when modal opens
  useEffect(() => {
    if (showModal) {
      fetchFiles();
    }
  }, [showModal, fetchFiles]);

  // Handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.alias || !uploadForm.date) {
      alert("Please fill all fields");
      return;
    }

    const formData = new FormData();
    formData.append("externalfile", uploadForm.file);
    formData.append("alias", uploadForm.alias);
    formData.append("date", uploadForm.date);
    formData.append("channel", "Channel Name"); // Adjust as needed
    formData.append("channel_id", channelId);

    try {
      const response = await authFetch("/api/external-data/upload", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();

      if (result.success) {
        alert("Data uploaded successfully");
        setUploadForm({ date: dayjs().format("YYYY-MM-DD"), alias: "", file: null });
        fetchFiles();
        // Refresh external data
        const dataResponse = await authFetch(
          `/api/external-data?channel_id=${channelId}&date=${startDateInput}`
        );
        const dataResult = await dataResponse.json();
        if (dataResult.success) {
          setExternalData(dataResult.data);
        }
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed");
    }
  };
    const handleAxisLabelRender = useCallback((args) => {
    if (args.axis?.orientation === "Vertical") {
      const value = Number(args.value);
      if (!isNaN(value)) {
        args.text = formatNumber(value);
      }
    }
  }, []);

  const handleTooltipRender = useCallback((args) => {
    if (!args.point) return;

    args.text = `${args.point.x} : ${formatNumber(args.point.y)}`;
  }, []);
  // Handle file delete
  const handleDelete = async (alias) => {
    if (!window.confirm(`Delete ${alias}?`)) return;

    try {
      const response = await authFetch(`/api/external-data/delete?alias=${encodeURIComponent(alias)}`, {
        method: "DELETE",
      });
      const result = await response.json();

      if (result.success) {
        alert("Data deleted successfully");
        fetchFiles();
        // Refresh external data
        const dataResponse = await authFetch(
          `/api/external-data?channel_id=${channelId}&date=${startDateInput}`
        );
        const dataResult = await dataResponse.json();
        if (dataResult.success) {
          setExternalData(dataResult.data);
        }
      }
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Delete failed");
    }
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!rawVideo || !startDateInput) return {};

    const startMinute = parseInt(startTime.split(":")[0]) * 60 + parseInt(startTime.split(":")[1]);
    const endMinute = parseInt(endTime.split(":")[0]) * 60 + parseInt(endTime.split(":")[1]);

    const base = dayjs(startDateInput, "YYYY-MM-DD");
    const yesterday = base.subtract(1, "day");
    const lastWeek = base.subtract(7, "day");

    // Get internal data
    const getInternalData = (date) => {
      const key = date.format("DD-MM-YYYY");
      const arr = rawVideo[key] || [];
      return arr.slice(startMinute, endMinute + 1).map((val, i) => ({
        x: minuteToLabel(startMinute + i),
        y: Math.round((Number(val) || 0) / 2),
      }));
    };

    const current = getInternalData(base);
    const yesterdayData = getInternalData(yesterday);
    const lastWeekData = getInternalData(lastWeek);

    // Get external data
    const getExternalData = (daysAgo) => {
      if (!externalData || externalData.length === 0) {
        return current.map((d) => ({ x: d.x, y: 0 }));
      }

      const targetDate = base.subtract(daysAgo, "day");
      const targetDateStr = targetDate.format("DD.MM.YYYY");

      // Find matching external data by date
      const match = externalData.find((item) => {
        const itemDate = dayjs(item.fileModel.date, "DD.MM.YYYY");
        return itemDate.format("DD.MM.YYYY") === targetDateStr;
      });

      if (!match || !match.value) {
        return current.map((d) => ({ x: d.x, y: 0 }));
      }

      return current.map((d, i) => ({
        x: d.x,
        y: match.value[startMinute + i] || 0,
      }));
    };

    const externalCurrent = getExternalData(0);
    const externalYesterday = getExternalData(1);
    const externalLastWeek = getExternalData(7);

    return {
      current,
      yesterday: yesterdayData,
      lastWeek: lastWeekData,
      externalCurrent,
      externalYesterday,
      externalLastWeek,
    };
  }, [rawVideo, startDateInput, startTime, endTime, externalData]);

  // Check if series has data
  const seriesHasData = (key) => {
    const data = chartData[key];
    return data && data.some((d) => d.y > 0);
  };

  // Toggle series visibility
  const toggleSeries = (key) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const hasData = Object.keys(chartData).length > 0 && 
    chartData.current && chartData.current.length > 0;

  // --- LOGGER INTEGRATION ---
  const { logPageChange } = useUserJourneyLogger({
    channelId,
  });
  useEffect(() => {
    logPageChange({
      chartType: "channelgridcomparison",
    });
  }, [logPageChange]);

  return (
    <div className="ccg-page" style={{ padding: "20px 0" }}>      
      <div style={{
      background: isDark ? "#1e293b" : "#ffffff",
      padding: "16px 20px",
      borderRadius: 12,
      marginBottom: 20,
      boxShadow: isDark
        ? "none"
        : "0 1px 3px rgba(0,0,0,0.1)",
      border: isDark ? "1px solid #334155" : "none",
      }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 auto", minWidth: 400 }}>
            <ChartControls
              channelId={channelId}
              mode="minute"
              onModeChange={() => {}}
              startDateInput={startDateInput}
              endDateInput={endDateInput}
              // Single-day mode: the picker only ever reports a start, and
              // this page defines the end as the same day.
              onStartDateChange={setEndDateInput}
              onEndDateChange={setEndDateInput}
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
              hideModeButtons={true}
              refreshEnabled={refreshEnabled}
              onRefreshToggle={setRefreshEnabled}
              refreshIntervalMin={refreshIntervalMin}
              onRefreshIntervalChange={setRefreshIntervalMin}
              countdown={refreshCountdown}
              onManualRefresh={manualRefresh}
            />
          </div>
          
          <button
            onClick={() => setShowModal(true)}
            style={{
              padding: "10px 24px",
              border: "none",
              borderRadius: 6,
              background: isDark ? "#2563eb" : "#3b82f6",
              color: "#fff",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Manage External Data
          </button>
        </div>
      </div>

      {hasData && (
        <div className="chart-container">
          <h3 className="chart-title">Channel Grid Comparison</h3>

          {loading && (
            <div
              style={{
                textAlign: "center",
                padding: "10px",
                color: isDark ? "#cbd5e1" : "#666",
              }}
            >
              Loading external data...
            </div>
          )}

          <ChartComponent
            ref={chartRef}
            width="100%"
            height="450px"
            margin={{ right: 40, left: 15 }}

            /* ---- removes white frame ---- */
            background={isDark ? "transparent" : "#ffffff"}
            chartArea={{
              background: "transparent",
              border: { width: 0 },
            }}

            primaryXAxis={{
              valueType: "Category",
              labelIntersectAction: "Rotate45",
              majorGridLines: { width: 0 },
              majorTickLines: { width: 0 },

              labelStyle: {
                color: isDark ? "#e5e7eb" : "#374151",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              },
            }}

            primaryYAxis={{
              title: "Avg Active TV",


              labelStyle: {
                color: isDark ? "#e5e7eb" : "#374151",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              },

              titleStyle: {
                color: isDark ? "#e5e7eb" : "#374151",
                fontWeight: "600",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              },

              majorTickLines: { width: 0 },
              lineStyle: { width: 0 },
            }}

            tooltip={{
              enable: true,
              shared: false,
            }}
            tooltipRender={handleTooltipRender}
            axisLabelRender={handleAxisLabelRender}
            legendSettings={{
              visible: false,
              textStyle: {
                color: isDark ? "#e5e7eb" : "#374151",
              },
            }}
          >
            <Inject services={[LineSeries, Category, Tooltip, Legend]} />

            <SeriesCollectionDirective>
              {SERIES_CONFIG.map((series) => {
                const data = chartData[series.key];
                const isVisible =
                  visibleSeries[series.key] && seriesHasData(series.key);

                return isVisible ? (
                  <SeriesDirective
                    key={series.key}
                    dataSource={data}
                    xName="x"
                    yName="y"
                    name={series.name}
                    type="Line"
                    width={2}
                    marker={{ visible: false }}
                    fill={series.color}
                    animation={{ enable: true, duration: 400}}
                  />
                ) : null;
              })}
            </SeriesCollectionDirective>
          </ChartComponent>

          {/* Toggle Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 20,
              marginTop: 20,
              flexWrap: "wrap",
            }}
          >
            {SERIES_CONFIG.map((series) => {
              const hasDataForSeries = seriesHasData(series.key);
              if (!hasDataForSeries) return null;

              const isActive = visibleSeries[series.key];

              return (
                <div
                  key={series.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSeries(series.key)}
                >
                  <div
                    style={{
                      width: 25,
                      height: 25,
                      backgroundColor: isActive
                        ? series.color
                        : isDark
                        ? "#334155"
                        : "#d1d5db",
                      border: `1px solid ${isDark ? "#475569" : "#ccc"}`,
                      borderRadius: 5,
                      transition: "all 0.2s ease",
                      transform: isActive ? "scale(1)" : "scale(0.95)",
                    }}
                  />

                  <span
                    style={{
                      fontSize: 14,
                      color: isDark ? "#e5e7eb" : "#374151",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {series.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {!hasData && (
        <div className="chart-container">
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            No data available for the selected date
          </div>
        </div>
      )}

      {/* Modal */}
    {showModal && (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(2,6,23,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
        onClick={() => setShowModal(false)}
      >
        <div
          style={{
            background: "var(--bg-card)",
            color: "var(--text-primary)",
            borderRadius: 12,
            padding: 40,
            maxWidth: 900,
            width: "90%",
            maxHeight: "90vh",
            overflow: "auto",
            boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 30,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 24,
                color: "var(--text-primary)",
              }}
            >
              Data Import
            </h2>

            <button
              onClick={() => setShowModal(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: 28,
                cursor: "pointer",
                color: "var(--text-secondary)",
              }}
            >
              ×
            </button>
          </div>

          {/* UPLOAD FORM */}
          <form onSubmit={handleUpload} style={{ marginBottom: 40 }}>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontWeight: 500,
                  color: "var(--text-primary)",
                }}
              >
                Please select a date:
              </label>

              <input
                type="date"
                value={uploadForm.date}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, date: e.target.value })
                }
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--border-color)",
                  borderRadius: 6,
                  fontSize: 14,
                  width: "200px",
                  background: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                }}
                required
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontWeight: 500,
                  color: "var(--text-primary)",
                }}
              >
                Import Data As:
              </label>

              <input
                type="text"
                value={uploadForm.alias}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, alias: e.target.value })
                }
                placeholder="Enter a name for this dataset"
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--border-color)",
                  borderRadius: 6,
                  fontSize: 14,
                  width: "100%",
                  maxWidth: "400px",
                  background: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                }}
                required
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontWeight: 500,
                  color: "var(--text-primary)",
                }}
              >
                Select a CSV File:
              </label>

              <input
                type="file"
                accept=".csv"
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, file: e.target.files[0] })
                }
                style={{ display: "none" }}
                id="csv-upload"
                required
              />

              <label
                htmlFor="csv-upload"
                style={{
                  display: "inline-block",
                  padding: "10px 24px",
                  background: "#3b82f6",
                  color: "#ffffff",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Choose File
              </label>

              {uploadForm.file && (
                <span
                  style={{
                    marginLeft: 12,
                    color: "var(--text-secondary)",
                    fontSize: 14,
                  }}
                >
                  {uploadForm.file.name}
                </span>
              )}
            </div>

            <button
              type="submit"
              style={{
                padding: "10px 32px",
                background: "#3b82f6",
                color: "#ffffff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Import
            </button>
          </form>

          {/* FILES TABLE */}
          <div
            style={{
              borderTop: "1px solid var(--border-color)",
              paddingTop: 30,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ background: "var(--bg-secondary)" }}>
                  <th
                    style={{
                      padding: 12,
                      textAlign: "left",
                      fontWeight: 600,
                      border: "1px solid var(--border-color)",
                      color: "var(--text-primary)",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: 12,
                      textAlign: "left",
                      fontWeight: 600,
                      border: "1px solid var(--border-color)",
                      color: "var(--text-primary)",
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: 12,
                      textAlign: "center",
                      fontWeight: 600,
                      border: "1px solid var(--border-color)",
                      color: "var(--text-primary)",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {files.length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: "var(--text-secondary)",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      No files uploaded yet
                    </td>
                  </tr>
                ) : (
                  files.map((file, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          padding: 12,
                          border: "1px solid var(--border-color)",
                          color: "var(--text-primary)",
                        }}
                      >
                        {file.alias}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          border: "1px solid var(--border-color)",
                          color: "var(--text-primary)",
                        }}
                      >
                        {file.date}
                      </td>
                      <td
                        style={{
                          padding: 12,
                          textAlign: "center",
                          border: "1px solid var(--border-color)",
                        }}
                      >
                        <button
                          onClick={() => handleDelete(file.alias)}
                          style={{
                            padding: "6px 16px",
                            background: "#ef4444",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}

    </div>
  );
}
